import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MultiSelect } from '@/components/ui/multi-select';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Plus, 
  X, 
  Calendar,
  User,
  Building2,
  Settings,
  Tag,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WorkOrderForm({ user, onClose, onWorkOrderCreated }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    type: 'Repair',
    priority: 'Medium',
    assignees: [],  // Changed from single assignee to array
    site: 'Main Site',
    department_id: '',
    machine_id: '',
    location: '',
    due_date: '',
    scheduled_start: '',
    scheduled_end: '',
    estimated_duration: '',
    description: '',
    safety_notes: '',
    checklist_items: [],
    tags: []
  });

  const [departments, setDepartments] = useState([]);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (formData.department_id) {
      fetchMachines(formData.department_id);
    } else {
      setMachines([]);
      setFormData(prev => ({ ...prev, machine_id: '' }));
    }
  }, [formData.department_id]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchMachines = async (departmentId) => {
    try {
      const response = await axios.get(`${API}/machines?department_id=${departmentId}`);
      setMachines(response.data);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/assignees`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching assignees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        assignees: formData.assignees.filter(id => id !== 'none'),  // Send array of assignees
        department_id: formData.department_id === 'none' ? null : formData.department_id,
        machine_id: formData.machine_id === 'none' ? null : formData.machine_id,
        estimated_duration: formData.estimated_duration ? Math.round(parseFloat(formData.estimated_duration) * 60) : null,  // Convert hours to minutes
        due_date: formData.due_date || null,
        scheduled_start: formData.scheduled_start || null,
        scheduled_end: formData.scheduled_end || null
      };

      const response = await axios.post(`${API}/work-orders`, submitData);
      
      // Dispatch event to notify Calendar and DailyTasks
      const createEvent = new CustomEvent('workOrderUpdated', {
        detail: response.data  // Send the complete new work order data
      });
      window.dispatchEvent(createEvent);
      
      toast.success('Work order created successfully!');
      
      if (onWorkOrderCreated) {
        onWorkOrderCreated(response.data);
      }
      
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create work order';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      checklist_items: [...prev.checklist_items, newChecklistItem.trim()]
    }));
    setNewChecklistItem('');
  };

  const removeChecklistItem = (index) => {
    setFormData(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (!newTag.trim() || formData.tags.includes(newTag.trim())) return;
    
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, newTag.trim()]
    }));
    setNewTag('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('createRepairWorkOrder')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('addNewRepairWorkOrder')}
              </p>
            </div>
          </div>
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="work-order-form">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">{t('basicInformation')}</h3>
        
        {/* Title */}
        <div>
          <Label htmlFor="title" className="text-sm font-medium text-gray-700">
            {t('titleRequired')}
          </Label>
          <Input
            id="title"
            type="text"
            placeholder={t('enterWorkOrderTitle')}
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            required
            className="mt-1"
            data-testid="wo-title"
          />
        </div>

        {/* Type and Priority */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('typeRequired')}</Label>
            <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
              <SelectTrigger className="mt-1" data-testid="wo-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PM">{t('preventiveMaintenance')}</SelectItem>
                <SelectItem value="Repair">{t('repair')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">{t('priority')}</Label>
            <Select value={formData.priority} onValueChange={(value) => handleChange('priority', value)}>
              <SelectTrigger className="mt-1" data-testid="wo-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">{t('priorityLow')}</SelectItem>
                <SelectItem value="Medium">{t('priorityMedium')}</SelectItem>
                <SelectItem value="High">{t('priorityHigh')}</SelectItem>
                <SelectItem value="Critical">{t('priorityCritical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Assignees - Multi-Select */}
        <div>
          <Label className="text-sm font-medium text-gray-700">{t('assigneeLabel')} (Multiple)</Label>
          <MultiSelect
            options={users.map(user => ({ value: user.id, label: user.name || user.username }))}
            value={formData.assignees}
            onChange={(selectedIds) => handleChange('assignees', selectedIds)}
            placeholder="Select assignees..."
            className="mt-1"
          />
        </div>
      </div>

      {/* Location Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">{t('locationEquipment')}</h3>
        
        {/* Site */}
        <div>
          <Label htmlFor="site" className="text-sm font-medium text-gray-700">
            {t('site')}
          </Label>
          <Input
            id="site"
            type="text"
            value={formData.site}
            onChange={(e) => handleChange('site', e.target.value)}
            className="mt-1"
            data-testid="wo-site"
          />
        </div>

        {/* Department and Machine */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">{t('departmentLabel')}</Label>
            <Select value={formData.department_id} onValueChange={(value) => handleChange('department_id', value)}>
              <SelectTrigger className="mt-1" data-testid="wo-department">
                <SelectValue placeholder={t('selectDepartmentOptional')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('noDepartment')}</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700">{t('machineLabel')}</Label>
            <Select 
              value={formData.machine_id} 
              onValueChange={(value) => handleChange('machine_id', value)}
              disabled={!formData.department_id}
            >
              <SelectTrigger className="mt-1" data-testid="wo-machine">
                <SelectValue placeholder={formData.department_id ? t('selectMachine') : t('selectDepartmentFirstMsg')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('noSpecificMachine')}</SelectItem>
                {machines.map(machine => (
                  <SelectItem key={machine.id} value={machine.id}>
                    {machine.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Location */}
        <div>
          <Label htmlFor="location" className="text-sm font-medium text-gray-700">
            {t('specificLocation')}
          </Label>
          <Input
            id="location"
            type="text"
            placeholder={t('specificLocationPlaceholder')}
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            className="mt-1"
            data-testid="wo-location"
          />
        </div>
      </div>

      {/* Scheduling */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">{t('schedulingSection')}</h3>
        
        {/* Due Date and Estimated Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="due-date" className="text-sm font-medium text-gray-700">
              {t('dueDate')}
            </Label>
            <Input
              id="due-date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              className="mt-1"
              data-testid="wo-due-date"
            />
          </div>

          <div>
            <Label htmlFor="estimated-duration" className="text-sm font-medium text-gray-700">
              Estimated Duration
            </Label>
            <div className="flex items-center space-x-2 mt-1">
              <Input
                id="estimated-duration"
                type="number"
                step="0.5"
                placeholder="e.g., 2.5"
                value={formData.estimated_duration}
                onChange={(e) => handleChange('estimated_duration', e.target.value)}
                className="flex-1"
                data-testid="wo-estimated-duration"
              />
              <span className="text-sm text-gray-500">hours</span>
            </div>
          </div>
        </div>

        {/* Scheduled Start */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div>
            <Label htmlFor="scheduled-start" className="text-sm font-medium text-gray-700">
              {t('scheduledStart')}
            </Label>
            <Input
              id="scheduled-start"
              type="datetime-local"
              value={formData.scheduled_start}
              onChange={(e) => handleChange('scheduled_start', e.target.value)}
              className="mt-1"
              data-testid="wo-scheduled-start"
            />
          </div>
        </div>
      </div>

      {/* Repair Instructions */}
      <div>
        <Label htmlFor="description" className="text-sm font-medium text-gray-700">
          {t('repairInstructions')}
        </Label>
        <Textarea
          id="description"
          placeholder={t('repairInstructionsPlaceholder')}
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={4}
          className="mt-1"
          data-testid="wo-description"
        />
      </div>

      {/* Safety Notes */}
      <div>
        <Label htmlFor="safety_notes" className="text-sm font-medium text-gray-700">
          {t('safetyNotes')}
        </Label>
        <Textarea
          id="safety_notes"
          placeholder={t('safetyNotesPlaceholder')}
          value={formData.safety_notes}
          onChange={(e) => handleChange('safety_notes', e.target.value)}
          rows={3}
          className="mt-1 bg-yellow-50"
          data-testid="wo-safety-notes"
        />
      </div>

      {/* Checklist */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700">{t('checklistItemsSection')}</Label>
        
        {/* Add new checklist item */}
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder={t('addChecklistItem')}
            value={newChecklistItem}
            onChange={(e) => setNewChecklistItem(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
            className="flex-1"
            data-testid="new-checklist-item"
          />
          <Button
            type="button"
            onClick={addChecklistItem}
            disabled={!newChecklistItem.trim()}
            size="sm"
            data-testid="add-checklist-item"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Display checklist items */}
        {formData.checklist_items.length > 0 && (
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {formData.checklist_items.map((item, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                <span className="text-sm text-gray-700">{item}</span>
                <Button
                  type="button"
                  onClick={() => removeChecklistItem(index)}
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700">{t('tagsSection')}</Label>
        
        {/* Add new tag */}
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder={t('addTag')}
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            className="flex-1"
            data-testid="new-tag"
          />
          <Button
            type="button"
            onClick={addTag}
            disabled={!newTag.trim() || formData.tags.includes(newTag.trim())}
            size="sm"
            data-testid="add-tag"
          >
            <Tag className="w-4 h-4" />
          </Button>
        </div>

        {/* Display tags */}
        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="flex items-center space-x-1">
                <span>{tag}</span>
                <Button
                  type="button"
                  onClick={() => removeTag(tag)}
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                >
                  <X className="w-2 h-2" />
                </Button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={loading}
          data-testid="cancel-work-order"
        >
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          disabled={loading || !formData.title.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="submit-work-order"
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{t('creating')}</span>
            </div>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              {t('createWorkOrder')}
            </>
          )}
        </Button>
      </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WorkOrderForm;